import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { IndianRupee, FileText, Users, BookOpen, TrendingUp, Pencil, Check, X, Loader2, Power, Download, Plus, Trash2 } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-overline uppercase text-gold font-bold">{label}</div>
        <Icon size={16} className="text-gold"/>
      </div>
      <div className="font-display text-3xl text-navy-deep mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// Sub-component — Per-paper rate editor table. Module-scoped to avoid re-mount keyboard bugs.
function PaperRateEditor({ papers, onSaved }) {
  const [editingId, setEditingId] = useState(null);
  const [draftRate, setDraftRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [newPaper, setNewPaper] = useState({ name: "", language: "Marathi", flat_rate: "", is_active: true });

  const startEdit = (p) => {
    setErr("");
    setEditingId(p.id);
    setDraftRate(String(p.flat_rate));
  };
  const cancelEdit = () => { setEditingId(null); setDraftRate(""); setErr(""); };

  const saveEdit = async (id) => {
    const val = parseFloat(draftRate);
    if (Number.isNaN(val) || val < 0) { setErr("Rate must be a non-negative number"); return; }
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/papers/${id}`, { flat_rate: val });
      cancelEdit();
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const togglePaper = async (p) => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/papers/${p.id}`, { is_active: !p.is_active });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deletePaper = async (p) => {
    const msg = `Delete newspaper "${p.name}"?\n\nIf bookings exist for this paper, it will be hidden from users instead of being permanently deleted.`;
    if (!window.confirm(msg)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/papers/${p.id}`);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const createPaper = async (e) => {
    e.preventDefault();
    const val = parseFloat(newPaper.flat_rate);
    if (!newPaper.name.trim()) { setErr("Name is required"); return; }
    if (!newPaper.language.trim()) { setErr("Language is required"); return; }
    if (Number.isNaN(val) || val < 0) { setErr("Rate must be a non-negative number"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/admin/papers", {
        name: newPaper.name.trim(),
        language: newPaper.language.trim(),
        flat_rate: val,
        is_active: newPaper.is_active,
      });
      setNewPaper({ name: "", language: "Marathi", flat_rate: "", is_active: true });
      setAddOpen(false);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="paper-rate-editor">
      <div className="px-6 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Newspaper Rates</div>
          <div className="text-xs text-slate-500 mt-0.5">{papers.length} papers · flat ₹ per booking (GST 5% applied at checkout)</div>
        </div>
        <button
          onClick={() => { setAddOpen((v) => !v); setErr(""); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-black text-white rounded-sm text-xs font-semibold hover:bg-gray-800 transition"
          data-testid="admin-paper-add-btn"
        >
          {addOpen ? <X size={14}/> : <Plus size={14}/>}
          {addOpen ? "Cancel" : "Add Newspaper"}
        </button>
      </div>

      {addOpen && (
        <form onSubmit={createPaper} className="mx-6 mb-3 p-4 border border-black/15 rounded-sm bg-slate-50" data-testid="admin-paper-add-form">
          <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold mb-3">Add a newspaper</div>
          <div className="grid sm:grid-cols-4 gap-3">
            <input
              type="text" required
              value={newPaper.name}
              onChange={(e) => setNewPaper({ ...newPaper, name: e.target.value })}
              placeholder="Newspaper name (e.g. Sakal)"
              className="col-span-2 px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
              data-testid="admin-paper-add-name"
            />
            <select
              value={newPaper.language}
              onChange={(e) => setNewPaper({ ...newPaper, language: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
              data-testid="admin-paper-add-language"
            >
              <option value="Marathi">Marathi</option>
              <option value="English">English</option>
              <option value="Hindi">Hindi</option>
            </select>
            <input
              type="number" min="0" step="50" required
              value={newPaper.flat_rate}
              onChange={(e) => setNewPaper({ ...newPaper, flat_rate: e.target.value })}
              placeholder="Flat rate (₹)"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white font-mono"
              data-testid="admin-paper-add-rate"
            />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={newPaper.is_active} onChange={(e) => setNewPaper({ ...newPaper, is_active: e.target.checked })}/>
              Active (visible to users)
            </label>
            <button
              type="submit" disabled={busy}
              className="ml-auto px-4 py-2 bg-black text-white rounded-sm text-xs font-semibold disabled:opacity-60 hover:bg-gray-800 flex items-center gap-1.5"
              data-testid="admin-paper-add-submit"
            >
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
              Add newspaper
            </button>
          </div>
        </form>
      )}
      {err && <div className="mx-6 mb-2 px-3 py-2 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs" data-testid="rate-editor-error">{err}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] tracking-overline uppercase text-slate-500 font-bold bg-slate-50">
            <tr>
              <th className="px-5 py-2.5">Paper</th>
              <th className="px-5 py-2.5">Lang</th>
              <th className="px-5 py-2.5">Flat ₹</th>
              <th className="px-5 py-2.5">Active</th>
              <th className="px-5 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {papers.map((p) => {
              const editing = editingId === p.id;
              return (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`paper-row-${p.id}`}>
                  <td className="px-5 py-2.5 font-medium text-navy-deep">{p.name}</td>
                  <td className="px-5 py-2.5 text-xs text-slate-500">{p.language}</td>
                  <td className="px-5 py-2.5">
                    {editing ? (
                      <input
                        type="number"
                        min="0"
                        step="50"
                        value={draftRate}
                        onChange={(e) => setDraftRate(e.target.value)}
                        className="w-28 px-2 py-1 border border-navy rounded-sm font-mono"
                        data-testid={`rate-input-${p.id}`}
                        autoFocus
                      />
                    ) : (
                      <span className="font-mono text-navy-deep">₹{Number(p.flat_rate).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    <button
                      onClick={() => togglePaper(p)}
                      disabled={busy}
                      className={`text-[10px] tracking-overline uppercase font-bold px-2 py-1 rounded-sm flex items-center gap-1 ${
                        p.is_active
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-500 border border-slate-200"
                      }`}
                      data-testid={`toggle-active-${p.id}`}
                    >
                      <Power size={10}/>{p.is_active ? "On" : "Off"}
                    </button>
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    {editing ? (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => saveEdit(p.id)}
                          disabled={busy}
                          className="px-2 py-1 bg-gold text-navy-deep rounded-sm text-xs hover:bg-gold-light flex items-center gap-1 disabled:opacity-60"
                          data-testid={`rate-save-${p.id}`}
                        >
                          {busy ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-2 py-1 border border-slate-300 rounded-sm text-xs hover:bg-slate-50 flex items-center gap-1"
                          data-testid={`rate-cancel-${p.id}`}
                        >
                          <X size={12}/> Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1 ml-auto">
                        <button
                          onClick={() => startEdit(p)}
                          className="px-2 py-1 border border-slate-300 rounded-sm text-xs hover:border-gold hover:text-gold flex items-center gap-1"
                          data-testid={`rate-edit-${p.id}`}
                        >
                          <Pencil size={12}/> Edit
                        </button>
                        <button
                          onClick={() => deletePaper(p)}
                          disabled={busy}
                          className="px-2 py-1 border border-red-200 text-red-700 rounded-sm text-xs hover:bg-red-50 flex items-center gap-1 disabled:opacity-60"
                          data-testid={`rate-delete-${p.id}`}
                          title="Delete this newspaper"
                        >
                          <Trash2 size={12}/> Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const loadAll = async () => {
    const [s, b, p] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data),
      api.get("/admin/bookings").then((r) => r.data),
      api.get("/papers").then((r) => r.data),
    ]);
    setStats(s); setBookings(b); setPapers(p);
  };

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, []);

  const downloadUsersExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/users/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `welworth-users-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-parchment">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-10 py-12 w-full">
        <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Control Room</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <h1 className="font-display text-4xl lg:text-5xl text-navy-deep mt-2 mb-8">Admin Dashboard</h1>
          <button
            onClick={downloadUsersExcel}
            disabled={downloading}
            className="mb-8 inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold px-5 py-2.5 rounded-sm text-sm disabled:opacity-60 transition"
            data-testid="admin-export-users"
          >
            {downloading ? <Loader2 size={16} className="animate-spin"/> : <Download size={16}/>}
            {downloading ? "Preparing…" : "Download Users Excel"}
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats-grid">
              <Stat icon={IndianRupee} label="Today's revenue" value={`₹${Number(stats?.today_revenue || 0).toLocaleString("en-IN")}`} sub={`${stats?.today_paid_count || 0} paid`} testid="stat-today-revenue"/>
              <Stat icon={FileText} label="This month bookings" value={stats?.month_bookings ?? 0} sub={`₹${Number(stats?.month_revenue || 0).toLocaleString("en-IN")} gross`} testid="stat-month-bookings"/>
              <Stat icon={Users} label="Total users" value={stats?.total_users ?? 0} testid="stat-total-users"/>
              <Stat icon={BookOpen} label="All-time bookings" value={stats?.total_bookings ?? 0} testid="stat-total-bookings"/>
            </div>

            {/* Rate editor + Paper-wise + Recent */}
            <div className="mt-10 space-y-8">
              <PaperRateEditor papers={papers} onSaved={loadAll} />

              <div className="grid lg:grid-cols-2 gap-8">
                <div className="bg-white border border-slate-200 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] tracking-overline uppercase text-gold font-bold">Paper-wise Revenue</div>
                    <TrendingUp size={14} className="text-gold"/>
                  </div>
                  {(stats?.paper_wise || []).length === 0 ? (
                    <div className="text-sm text-slate-500">No paid bookings yet.</div>
                  ) : (
                    <div className="space-y-3" data-testid="paper-wise-list">
                      {(stats.paper_wise || []).map((p) => {
                        const max = Math.max(...stats.paper_wise.map(x => x.revenue));
                        const pct = max > 0 ? (p.revenue / max) * 100 : 0;
                        return (
                          <div key={p.paper}>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-navy-deep font-medium">{p.paper}</span>
                              <span className="font-mono text-slate-600">₹{Number(p.revenue).toLocaleString("en-IN")} · {p.count}</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-sm mt-1.5 overflow-hidden">
                              <div className="h-full bg-gold" style={{ width: `${pct}%` }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div className="px-6 pt-5 pb-3 text-[10px] tracking-overline uppercase text-gold font-bold">Recent Bookings</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[10px] tracking-overline uppercase text-slate-500 font-bold bg-slate-50">
                        <tr>
                          <th className="px-5 py-2.5">Customer</th>
                          <th className="px-5 py-2.5">Paper</th>
                          <th className="px-5 py-2.5 text-right">₹</th>
                          <th className="px-5 py-2.5">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.slice(0, 10).map((b) => (
                          <tr key={b.id} className="border-t border-slate-100">
                            <td className="px-5 py-2.5 truncate max-w-[150px]">{b.advocate?.name}</td>
                            <td className="px-5 py-2.5 truncate max-w-[120px]">{b.paper_name}</td>
                            <td className="px-5 py-2.5 text-right font-mono">₹{Number(b.total_amount).toFixed(0)}</td>
                            <td className="px-5 py-2.5">
                              <span className={`text-[9px] tracking-overline uppercase font-bold px-1.5 py-0.5 rounded-sm ${
                                b.payment_status?.startsWith("paid")
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}>{b.status}</span>
                            </td>
                          </tr>
                        ))}
                        {bookings.length === 0 && (
                          <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-500">No bookings yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
